// frontend/src/components/IntroGuide/IntroGuide.js

import React, { useState, useEffect } from 'react';
import { CeMark, MdSymbol } from '../common/MedicalDeviceMarks';
import { PRODUCT_INFO } from '../../constants/productInfo';
import './IntroGuide.css';

const IntroGuide = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Don't render if not open
  if (!isOpen) return null;

  const tabs = [
    { id: 'overview', label: 'Översikt', icon: 'fas fa-home' },
    { id: 'patient', label: 'Patientmöte', icon: 'fas fa-microphone' },
    { id: 'support', label: 'Support', icon: 'fas fa-life-ring' },
    { id: 'free', label: 'Fri Diktering', icon: 'fas fa-comments' },
    { id: 'regulatory', label: 'Bruksanvisning', icon: 'fas fa-file-medical' }
  ];

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="guide-content">
            <h2>Välkommen till Aion Notes</h2>
            <div className="content-section">
              <h3>Om Aion Notes</h3>
              <p>
                Aion Notes är en avancerad medicinsk journalapplikation som använder AI-teknik
                för att automatiskt transkribera och strukturera dina patientsamtal. Med vår app
                kan du fokusera helt på patienten medan tekniken tar hand om dokumentationen.
              </p>
            </div>
            
            <div className="content-section">
              <h3>Arbetsflöde</h3>
              <ol>
                <li><strong>Tryck på "Nytt patientmöte"</strong> för att starta en ny session</li>
                <li><strong>Välj att spela in</strong> och genomför ditt patientsamtal som vanligt</li>
                <li><strong>Journal genereras automatiskt</strong> efter avslutat samtal baserat på vald mall</li>
                <li><strong>Redigera journalen</strong> innan du för över den till ditt journalsystem</li>
              </ol>
            </div>

            <div className="content-section">
              <h3>Fördelar med Aion Notes</h3>
              <div className="benefits-grid">
                <div className="benefit-item">
                  <i className="fas fa-clock"></i>
                  <h4>Spartid</h4>
                  <p>Spendera upp till 70% mindre tid på dokumentation</p>
                </div>
                <div className="benefit-item">
                  <i className="fas fa-user-md"></i>
                  <h4>Bättre patientkontakt</h4>
                  <p>Fokusera på patienten istället för skärmen</p>
                </div>
                <div className="benefit-item">
                  <i className="fas fa-check-circle"></i>
                  <h4>Högre kvalitet</h4>
                  <p>Mer strukturerade och kompletta journalanteckningar</p>
                </div>
                <div className="benefit-item">
                  <i className="fas fa-shield-alt"></i>
                  <h4>GDPR-säker</h4>
                  <p>Följer GDPR:s standard för säker datahantering</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'patient':
        return (
          <div className="guide-content">
            <h2>Patientmöte - Komplett workflow</h2>
            <div className="content-section">
              <h3>Steg 1: Starta inspelningen</h3>
              <p>
                Börja genom att välja Nytt patientmöte från sidomenyn. Välj rätt journalmall från rullgardinsmenyn. Kontrollera att 
                rätt mikrofon är vald. När du pratar ska du se att den blåa cirkeln rör sig i takt med ditt tal.
              </p>
            </div>

            <div className="content-section">
              <h3>Steg 2: Genomför samtalet</h3>
              <p>
                Tryck på "Starta inspelning" och genomför ditt patientsamtal som vanligt.
                Appen kommer automatiskt att transkribera allt som sägs. Du kan pausa och
                återuppta inspelningen vid behov.
              </p>
              <ul>
                <li>Prata naturligt och tydligt</li>
                <li>Förstärk gärna det patienten säger genom att upprepa viktiga detaljer
                    <br/><em>Exempel: Om patienten säger "Jag har haft ont i höger knä länge",</em> 
                    <br/>kan du förstärka med detta genom att lägga till "höger knä" i dina frågr:
                    <br/><em>"Hur länge har du haft ont i höger knä?"</em> istället för <em> "Hur länge har du haft ont i knät?"</em>
                    <br/>Detta hjälper Aion att plocka upp vad du som behandlare tycker är viktigt i samtalet</li>
              </ul>
              <p>
                <strong>Efter att patienten lämnat rummet:</strong>
              </p>
              <ul>
                <li>Du kan fortsätta inspelningen och upprepa eller gå igenom detaljer som ska med i journalen</li>
                <li>Detta är ett bra tillfälle att nämna ortopediska test, kliniska fynd eller annat som du
                    inte nödvändigtvis vill säga högt när patienten är i rummet</li>
                <li>Aion Notes organiserar innehållet automatiskt - du kan börja med planering och avsluta
                    med anamnes om du vill. Ordningen spelar ingen roll, allt hamnar rätt i journalen</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>Steg 3: Generera journal</h3>
              <p>
                När samtalet är avslutat trycker du på "Avsluta och skapa anteckning". 
                Aion kommer då att strukturera texten enligt den valda mallen och skapa 
                en färdig journal som du kan redigera och anpassa.
              </p>
            </div>

            <div className="content-section">
              <h3>Steg 4: Redigera journalen</h3>
              <p>
                I journaleditorn kan du:
              </p>
              <ul>
                <li>Redigera texten direkt i de olika sektionerna</li>
                <li>Använda "Justera"-funktionen för att Aion notes ska assistera i din redigering</li>
                <li>Generera dokument som remissunderlag, remissvar eller andra specifika dokument som baseras på journalen</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>Steg 5: För över till journalsystemet</h3>
              <p>
                När du är nöjd med journalen:
              </p>
              <ol>
                <li>Öppna en tom mall i ditt journalsystem</li>
                <li>Gå tillbaka till Aion Notes</li>
                <li>Tryck på "För över till journalsystem"</li>
                <li>Aion sköter resten automatiskt!</li>
              </ol>
              <p style={{marginTop: '20px'}}>
                <strong>Viktigt: Aion Notes är tränad för att förstå och skriva medicinskt relevanta journaler,
                men kan ibland misstolka eller missa detaljer. Som legitimerad terapeut är det alltid du som är
                ansvarig för innehållet i den journal du för över till ditt journalsystem. Granska alltid
                journalen noggrant innan överföring.</strong>
              </p>
            </div>
          </div>
        );

      case 'support':
        return (
          <div className="guide-content">
            <h2>Support - Få hjälp när du behöver det</h2>
            
            <div className="content-section">
              <h3>När ska du kontakta support?</h3>
              <p>
                Vi finns här för att hjälpa dig med alla typer av frågor och problem. 
                Använd vårt ticket-system för snabb och effektiv hjälp.
              </p>
            </div>

            <div className="content-section">
              <h3>Tillgängliga kategorier</h3>
              <ul>
                <li><strong>Teknisk support</strong> - Problem med inloggning, prestanda eller tekniska fel</li>
                <li><strong>Funktionsfråga</strong> - Frågor om hur funktioner fungerar eller används</li>
                <li><strong>Bugg</strong> - Rapportera fel eller oväntade beteenden i systemet</li>
                <li><strong>Lägg till medicinsk term</strong> - Föreslå medicinska termer som ska läggas till i systemet</li>
                <li><strong>Annat</strong> - Övriga frågor eller förslag</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>Hur skapar du en ticket?</h3>
              <ol>
                <li>Klicka på "Support" i sidomenyn</li>
                <li>Välj lämplig kategori för ditt ärende</li>
                <li>Fyll i din e-postadress för svar</li>
                <li>För vanliga ärenden: Skriv ett ämne och beskriv problemet detaljerat</li>
                <li>För medicinska termer: Fyll i den felstavade termen och den korrekta stavningen</li>
                <li>Skicka in din ticket</li>
              </ol>
            </div>

            <div className="content-section">
              <h3>För medicinska termer</h3>
              <p>
                Om du vill lägga till eller korrigera medicinska termer behöver du ange:
              </p>
              <ul>
                <li><strong>Felstavad term</strong> - Hur termen blev feltranskriberad</li>
                <li><strong>Korrekt stavning</strong> - Hur termen ska stavas</li>
                <li><strong>Typ av term</strong> - Medicin, diagnos, test, anatomi etc.</li>
                <li><strong>Profession</strong> - Vilken yrkesgrupp som främst använder termen</li>
                <li><strong>Kontext</strong> - I vilket sammanhang termen används (valfritt)</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>Tips för snabbare hjälp</h3>
              <ul>
                <li>Beskriv problemet så detaljerat som möjligt</li>
                <li>Inkludera steg för att återskapa problemet vid buggar</li>
                <li>Ange vilken webbläsare och version du använder vid tekniska problem</li>
                <li>Vid medicinska termer: Ge exempel på sammanhang där termen används</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>Svarstider</h3>
              <p>
                Vi strävar efter att svara på alla tickets inom 24 timmar under vardagar.
                Akuta tekniska problem prioriteras högst.
              </p>
            </div>
          </div>
        );

      case 'free':
        return (
          <div className="guide-content">
            <h2>Fri Diktering - Flexibel Dokumentation</h2>
            <div className="content-section">
              <h3>När används Fri Diktering?</h3>
              <p>
                Fri Diktering är idealisk för situationer där du vill diktera text fritt
                utan strukturerade avsnitt. Prata kring patienten och journalen i den ordning
                som passar dig bäst. Du behöver inte tänka på formalia eller i vilken ordning
                du säger saker och ting.
              </p>
              <p>
                <em>Exempel: Diktera som att du skulle prata med en kollega om patienten.
                Aion Notes kommer att plocka upp allting och strukturera det automatiskt.
                Du kan hoppa mellan anamnes, status, planering och andra delar helt fritt.</em>
              </p>
            </div>

            <div className="content-section">
              <h3>Så här använder du Fri Diktering</h3>
              <ol>
                <li>Välj funktionen "Nytt patientmöte" från menyn</li>
                <li>Välj en lämplig mall eller använd en generisk mall</li>
                <li>Starta inspelningen och prata fritt</li>
                <li>Aion Notes kommer att transkribera allt du säger</li>
                <li>Redigera och strukturera texten i journaleditorn efteråt</li>
              </ol>
            </div>

          </div>
        );

      case 'regulatory':
        return (
          <div className="guide-content">
            <h2>Bruksanvisning &amp; produktinformation</h2>

            <div className="content-section regulatory-marks">
              <CeMark size={40} />
              <MdSymbol size={40} />
              <span className="regulatory-marks-caption">
                {PRODUCT_INFO.name} v{PRODUCT_INFO.version} – Medicinteknisk produkt,
                klass {PRODUCT_INFO.riskClass} (MDR 2017/745)
              </span>
            </div>

            <div className="content-section">
              <h3>Avsett ändamål</h3>
              <p>{PRODUCT_INFO.intendedPurpose}</p>
            </div>

            <div className="content-section">
              <h3>Avsedd användargrupp</h3>
              <p>
                Legitimerade fysioterapeuter och legitimerade psykologer – legitimerad
                hälso- och sjukvårdspersonal med ansvar för journalföring, vana vid
                digitala journalsystem och förståelse för dokumentationskrav.
              </p>
              <p>
                Systemet är <strong>inte</strong> avsett för patienter, administrativ
                personal utan journalansvar eller lekmän.
              </p>
            </div>

            <div className="content-section">
              <h3>Vad produkten inte gör</h3>
              <p>Aion Notes är ett dokumentationsverktyg. Produkten är inte avsedd att:</p>
              <ul>
                <li>ställa diagnos eller föreslå diagnoser,</li>
                <li>ge behandlingsrekommendationer,</li>
                <li>fatta eller föreslå kliniska beslut,</li>
                <li>prioritera patienter eller triagera,</li>
                <li>generera varningar eller larm baserade på patientdata,</li>
                <li>ersätta medicinsk bedömning,</li>
                <li>automatiskt föra in text i patientjournal utan användarens aktiva godkännande.</li>
              </ul>
              <p>
                All klinisk bedömning och allt ansvar för journalinnehåll vilar på
                användaren.
              </p>
            </div>

            <div className="content-section">
              <h3>Varningar och säker användning</h3>
              <p className="regulatory-warning">
                Texten är AI-genererad och kan innehålla fel. Granska noggrant innan
                godkännande.
              </p>
              <ol>
                <li>
                  Granska alltid all AI-genererad text noggrant innan godkännande.
                  Texten är ett förslag och kan innehålla fel.
                </li>
                <li>
                  Du ansvarar alltid för journalinnehållets korrekthet, oavsett om
                  texten är AI-genererad eller manuellt skriven.
                </li>
                <li>
                  Kontrollera att rätt patient är vald i journalsystemet innan du
                  överför journaltext.
                </li>
                <li>
                  Var medveten om att systemet kan producera text som inte har stöd i
                  samtalet (hallucination). Verifiera mot ditt eget minne av samtalet.
                </li>
                <li>
                  Kontrollera att inspelningen startade korrekt och att transkriberingen
                  är komplett innan du godkänner.
                </li>
                <li>Använd mallar som är anpassade för din verksamhet och ditt kliniska sammanhang.</li>
                <li>Rapportera fel och oväntade beteenden via den inbyggda felrapporteringsfunktionen.</li>
                <li>
                  Systemet ersätter inte din medicinska bedömning. Använd det som ett
                  dokumentationsverktyg – det går inte att använda som beslutsstöd.
                </li>
                <li>
                  Använd ett starkt lösenord. Obehörig åtkomst motverkas av starka
                  lösenord.
                </li>
              </ol>
            </div>

            <div className="content-section">
              <h3>Tillverkare och kontakt</h3>
              <p>
                {PRODUCT_INFO.manufacturer.name} (org.nr {PRODUCT_INFO.manufacturer.orgNumber})
                <br />
                {PRODUCT_INFO.manufacturer.address}
                <br />
                {PRODUCT_INFO.manufacturer.email}
              </p>
            </div>

            <div className="content-section">
              <h3>Identifiering</h3>
              <ul>
                <li>Produkt: {PRODUCT_INFO.name} – {PRODUCT_INFO.productType}</li>
                <li>Version: {PRODUCT_INFO.version} ({PRODUCT_INFO.versionDate})</li>
                <li>Riskklass: Klass {PRODUCT_INFO.riskClass} – {PRODUCT_INFO.classificationRule}</li>
                <li>UDI-DI: {PRODUCT_INFO.udiDi}</li>
                <li>Basic UDI-DI: {PRODUCT_INFO.basicUdiDi}</li>
                <li>SRN: {PRODUCT_INFO.srn}</li>
              </ul>
            </div>
          </div>
        );

      default:
        return <div>Innehåll kunde inte laddas</div>;
    }
  };

  return (
    <div className="intro-guide-overlay" onClick={handleOverlayClick}>
      <div className="intro-guide-modal">
        <div className="intro-guide-header">
          <div className="intro-guide-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`intro-guide-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={tab.icon}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
          <button className="intro-guide-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="intro-guide-body">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default IntroGuide;